import { useState } from "react";
import dayjs from "dayjs";

import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Card from "react-bootstrap/Card";
import { FaDatabase } from "react-icons/fa";

import { Paginated } from "@/@types/generics/paginated";
import { GenericObject } from "@/@types/generics/genericals";
import { getQuarterDates } from "@/utils/quarters";
import { exportExcel } from "@/utils/exports";
import { calculatePercentage } from "@/utils/calculatePercentage";
import { getIndicatorClassification, IndicatorEsfEnum } from "@/utils/indicatorsEsf";
import { ResumeIndicatorC5ByHealthTeamData } from "@/services/esus/indicator/esf/c5/getResumeIndicatorC5ByHealthTeam";

import { IndicatorEsfC5HealthTeamsFilters } from "./IndicatorEsfC5HealthTeamsFilters";
import { IndicatorEsfC5HealthTeamsClassificationTable } from "./IndicatorEsfC5HealthTeamsTable";
import { PaginationLinks } from "@/components/PaginationLinks";
import { NoRecordsFeedback } from "@/components/NoRecordsFeedback";
import { LoadingScreen } from "@/components/LoadingScreen";

type IndicatorEsfC5HealthTeamsProps = {
	selectedQuarter: string;
	search: string;
	setSearch: (search: string) => void;
	isLoading: boolean;
	paginatedHealthTeamsClassifications: Paginated<ResumeIndicatorC5ByHealthTeamData>;
	pageNumber: number;
	setPageNumber: (pageNumber: number) => void;
	handleGetAllResumeIndicatorC5ByHealthTeam: () => Promise<ResumeIndicatorC5ByHealthTeamData[]>;
};

export function IndicatorEsfC5HealthTeams({
	selectedQuarter,
	search,
	setSearch,
	isLoading,
	paginatedHealthTeamsClassifications,
	pageNumber,
	setPageNumber,
	handleGetAllResumeIndicatorC5ByHealthTeam,
}: Readonly<IndicatorEsfC5HealthTeamsProps>) {
	const [isLoadingExportData, setIsLoadingExportData] = useState(false);
	async function handleExportTable() {
		setIsLoadingExportData(true);
		const healthTeamsClassifications = await handleGetAllResumeIndicatorC5ByHealthTeam();
		setIsLoadingExportData(false);
		const quarterDates = getQuarterDates(selectedQuarter);

		if (Object.keys(healthTeamsClassifications).length > 0) {
			const excelData: GenericObject[] = transformIndicatorC5AttendancesToGenericObjectArray(
				healthTeamsClassifications
			);
			exportExcel(
				excelData,
				`Pontuações por equipe - ${dayjs(quarterDates.startDate).format(
					"DD-MM-YYYY"
				)} à ${dayjs(quarterDates.endDate).format("DD/MM/YYYY")}`
			);
		}
	}

	function transformIndicatorC5AttendancesToGenericObjectArray(
		healthTeamClassifications: ResumeIndicatorC5ByHealthTeamData[]
	) {
		return healthTeamClassifications.map(
			(healthTeamClassification) =>
				({
					"Equipe do atendimento": healthTeamClassification.healthTeam?.name ?? "",
					"Total de hipertensos":
						healthTeamClassification.totalHypertensiveCitizensInPeriod,
					Pontuação: healthTeamClassification.totalHypertensiveCitizensInPeriodScore,
					"% Classificação": `${calculatePercentage(
						healthTeamClassification.totalHypertensiveCitizensInPeriodScore,
						healthTeamClassification.maxHypertensiveCitizensInPeriodScore
					)}%`,
					Classificação: getIndicatorClassification(
						IndicatorEsfEnum.C5,
						calculatePercentage(
							healthTeamClassification.totalHypertensiveCitizensInPeriodScore,
							healthTeamClassification.maxHypertensiveCitizensInPeriodScore
						)
					),
				} as GenericObject)
		);
	}

	return (
		<Row className="bg-white m-3 p-2">
			<Col>
				<LoadingScreen loading={isLoadingExportData} />
				<Card>
					<Card.Header>
						<IndicatorEsfC5HealthTeamsFilters
							search={search}
							setSearch={setSearch}
							handleExportTable={handleExportTable}
						/>
					</Card.Header>
					<Card.Body>
						<IndicatorEsfC5HealthTeamsClassificationTable
							healthTeamsClassifications={paginatedHealthTeamsClassifications.data}
							isLoading={isLoading}
						/>
					</Card.Body>
					<Card.Footer>
						{paginatedHealthTeamsClassifications?.totalRecords ? (
							<PaginationLinks
								itemsPerPage={paginatedHealthTeamsClassifications.pageSize}
								totalPages={paginatedHealthTeamsClassifications.totalRecords}
								changeSelectedPage={(pageNumber) => {
									setPageNumber(pageNumber);
								}}
								pageNumber={pageNumber || 0}
							/>
						) : (
							!isLoading && (
								<NoRecordsFeedback
									message={"Nenhum resultado encontrado."}
									icon={<FaDatabase />}
								/>
							)
						)}
					</Card.Footer>
				</Card>
			</Col>
		</Row>
	);
}
